import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Briefcase, Calendar, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { computeObraInsights } from "@/lib/obraInsights";
import { ObraConclusiones } from "@/components/obras/ObraConclusiones";

interface ObraCliente {
  id: string;
  numero_obra: string | null;
  nombre: string;
  estado: 'activa' | 'pausada' | 'finalizada' | 'cancelada';
  fecha_inicio: string;
  fecha_fin_estimada: string | null;
  fecha_fin_real: string | null;
  horas_estimadas: number;
  horas_consumidas: number;
  coste_mano_obra_estimado: number;
  coste_consumido: number;
  precio_venta: number | null;
  entrega_pospuesta: boolean;
  tiene_docs_entrega: boolean;
}

interface Props {
  clienteId: string;
  clienteNombre: string;
}

type Filtro = 'todas' | 'activa' | 'finalizada' | 'cancelada';

const ESTADO_BADGE: Record<string, { label: string; cls: string }> = {
  activa: { label: 'Activa', cls: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  pausada: { label: 'Pausada', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  finalizada: { label: 'Finalizada', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  cancelada: { label: 'Cancelada', cls: 'bg-muted text-muted-foreground border-border' },
};

const fmtEur = (n: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
const fmtPct = (n: number) => `${n > 0 ? '+' : ''}${n.toFixed(0)}%`;

export function ObrasClienteTab({ clienteId, clienteNombre }: Props) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [obras, setObras] = useState<ObraCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<Filtro>('todas');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data, error } = await (supabase as any)
        .from('obras')
        .select('id, numero_obra, nombre, estado, fecha_inicio, fecha_fin_estimada, fecha_fin_real, horas_estimadas, horas_consumidas, coste_mano_obra_estimado, coste_consumido, precio_venta, entrega_pospuesta')
        .eq('cliente_id', clienteId)
        .order('fecha_inicio', { ascending: false });
      if (error) {
        toast({ title: 'Error', description: error.message, variant: 'destructive' });
        setLoading(false);
        return;
      }
      const ids = (data ?? []).map((o: any) => o.id);
      let docsByObra: Record<string, number> = {};
      if (ids.length > 0) {
        const { data: docs } = await (supabase as any)
          .from('obra_entrega_documentos')
          .select('obra_id')
          .in('obra_id', ids);
        (docs ?? []).forEach((d: any) => { docsByObra[d.obra_id] = (docsByObra[d.obra_id] ?? 0) + 1; });
      }
      const mapped: ObraCliente[] = (data ?? []).map((o: any) => ({
        id: o.id,
        numero_obra: o.numero_obra ?? null,
        nombre: o.nombre,
        estado: o.estado,
        fecha_inicio: o.fecha_inicio,
        fecha_fin_estimada: o.fecha_fin_estimada,
        fecha_fin_real: o.fecha_fin_real,
        horas_estimadas: Number(o.horas_estimadas) || 0,
        horas_consumidas: Number(o.horas_consumidas) || 0,
        coste_mano_obra_estimado: Number(o.coste_mano_obra_estimado) || 0,
        coste_consumido: Number(o.coste_consumido) || 0,
        precio_venta: o.precio_venta != null ? Number(o.precio_venta) : null,
        entrega_pospuesta: !!o.entrega_pospuesta,
        tiene_docs_entrega: (docsByObra[o.id] ?? 0) > 0,
      }));
      setObras(mapped);
      setLoading(false);
    };
    load();
  }, [clienteId, toast]);

  const filtradas = useMemo(() => {
    if (filtro === 'todas') return obras;
    return obras.filter(o => o.estado === filtro);
  }, [obras, filtro]);

  const resumen = useMemo(() => {
    const total = obras.length;
    const activas = obras.filter(o => o.estado === 'activa').length;
    const finalizadas = obras.filter(o => o.estado === 'finalizada');
    const costeTotal = obras.reduce((s, o) => s + o.coste_consumido, 0);
    const ventasTotal = finalizadas.reduce((s, o) => s + (o.precio_venta ?? 0), 0);
    const costeFinalizadas = finalizadas.reduce((s, o) => s + o.coste_consumido, 0);
    const margenTotal = ventasTotal - costeFinalizadas;
    const finalizadasConPrecio = finalizadas.filter(o => o.precio_venta != null && o.precio_venta > 0).length;
    return { total, activas, finalizadas: finalizadas.length, costeTotal, margenTotal, finalizadasConPrecio };
  }, [obras]);

  const counts = useMemo(() => ({
    todas: obras.length,
    activa: obras.filter(o => o.estado === 'activa').length,
    finalizada: obras.filter(o => o.estado === 'finalizada').length,
    cancelada: obras.filter(o => o.estado === 'cancelada').length,
  }), [obras]);

  if (loading) {
    return <p className="text-sm text-muted-foreground p-4">Cargando obras...</p>;
  }

  if (obras.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Briefcase className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            {clienteNombre} todavía no tiene obras registradas.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* KPIs resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total obras</p>
            <p className="text-2xl font-bold">{resumen.total}</p>
            <p className="text-xs text-muted-foreground mt-1">{resumen.activas} activa{resumen.activas !== 1 && 's'} · {resumen.finalizadas} finalizada{resumen.finalizadas !== 1 && 's'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Coste total acumulado</p>
            <p className="text-2xl font-bold">{fmtEur(resumen.costeTotal)}</p>
            <p className="text-xs text-muted-foreground mt-1">Mano de obra + materiales</p>
          </CardContent>
        </Card>
        <Card className="col-span-2">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Margen total (obras finalizadas)</p>
            <p className={`text-2xl font-bold ${resumen.margenTotal >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
              {resumen.margenTotal >= 0 ? '+' : ''}{fmtEur(resumen.margenTotal)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {resumen.finalizadasConPrecio === 0
                ? 'Añade el precio de venta a las obras finalizadas para ver el margen.'
                : `Calculado sobre ${resumen.finalizadasConPrecio} obra${resumen.finalizadasConPrecio !== 1 ? 's' : ''} con precio de venta.`}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Tabs value={filtro} onValueChange={(v) => setFiltro(v as Filtro)}>
        <TabsList>
          <TabsTrigger value="todas">Todas ({counts.todas})</TabsTrigger>
          <TabsTrigger value="activa">Activas ({counts.activa})</TabsTrigger>
          <TabsTrigger value="finalizada">Finalizadas ({counts.finalizada})</TabsTrigger>
          {counts.cancelada > 0 && <TabsTrigger value="cancelada">Canceladas ({counts.cancelada})</TabsTrigger>}
        </TabsList>
      </Tabs>

      {/* Tarjetas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {filtradas.map(o => <ObraCard key={o.id} obra={o} onOpen={() => navigate('/obras')} />)}
      </div>
      {filtradas.length === 0 && (
        <p className="text-sm text-muted-foreground italic text-center py-6">No hay obras en este filtro.</p>
      )}
    </div>
  );
}

function ObraCard({ obra, onOpen }: { obra: ObraCliente; onOpen: () => void }) {
  const badge = ESTADO_BADGE[obra.estado];
  const pctHoras = obra.horas_estimadas > 0
    ? (obra.horas_consumidas / obra.horas_estimadas) * 100
    : 0;

  const { margen, margenPct, conclusiones } = computeObraInsights(obra);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0 flex-1">
            {obra.numero_obra && (
              <p className="text-[10px] font-mono text-muted-foreground">{obra.numero_obra}</p>
            )}
            <CardTitle className="text-base truncate">{obra.nombre}</CardTitle>
            <CardDescription className="flex items-center gap-1 text-xs mt-0.5">
              <Calendar className="h-3 w-3" />
              {format(new Date(obra.fecha_inicio), "d MMM yyyy", { locale: es })}
              {(obra.fecha_fin_real || obra.fecha_fin_estimada) && (
                <> → {format(new Date(obra.fecha_fin_real || obra.fecha_fin_estimada!), "d MMM yyyy", { locale: es })}</>
              )}
            </CardDescription>
          </div>
          <Badge variant="outline" className={badge.cls}>{badge.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Métricas */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">Horas</p>
            <p className="font-semibold tabular-nums">
              {obra.horas_consumidas.toFixed(1)} / {obra.horas_estimadas.toFixed(1)} h
            </p>
            {obra.horas_estimadas > 0 && (
              <Progress value={Math.min(pctHoras, 100)} className="h-1.5 mt-1" />
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Coste real</p>
            <p className="font-semibold tabular-nums">{fmtEur(obra.coste_consumido)}</p>
            {obra.coste_mano_obra_estimado > 0 && (
              <p className="text-xs text-muted-foreground">Estim. {fmtEur(obra.coste_mano_obra_estimado)}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Precio venta</p>
            <p className="font-semibold tabular-nums">
              {obra.precio_venta != null ? fmtEur(obra.precio_venta) : <span className="text-muted-foreground italic font-normal">Sin definir</span>}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Margen</p>
            {margen != null ? (
              <p className={`font-semibold tabular-nums ${margen >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
                {margen >= 0 ? '+' : ''}{fmtEur(margen)}
                {margenPct != null && <span className="text-xs ml-1 opacity-80">({margenPct.toFixed(0)}%)</span>}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground italic">—</p>
            )}
          </div>
        </div>

        <ObraConclusiones conclusiones={conclusiones} />

        <div className="flex justify-end pt-1">
          <Button size="sm" variant="ghost" onClick={onOpen} className="h-7 text-xs">
            Ver obra <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
